import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './AdInterstitial.css';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AdDisplay } from "./AdDisplay";
import { RtbAd } from "./RtbAd";

const AdInterstitial = ({ 
  zoneId = "interstitial", 
  onClose, 
  showTimer = true, 
  timerDuration = 10,
  autoCloseOnNoAd = true,
  onAdLoadResult = null, // Nouveau callback pour informer si une pub a été chargée
  className = "",
  style = {}
}) => {
  const [ad, setAd] = useState(null);
  const containerRef = useRef(null);
  const [timer, setTimer] = useState(timerDuration);
  const [dimensions, setDimensions] = useState({ width: "100vw", height: "100vh" });
  const [dimensionsReady, setDimensionsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adLoadChecked, setAdLoadChecked] = useState(false);

  // Mettre à jour les dimensions du conteneur
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        setDimensions({
          width: `${width}px`,
          height: `${height}px`,
        });
        setDimensionsReady(true);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
      setDimensionsReady(false);
    };
  }, []);

  // Récupérer une publicité
  const fetchAd = async () => {
    setIsLoading(true);
    try {
      const width = parseInt(dimensions.width, 10);
      const height = parseInt(dimensions.height, 10);
      
      // Déterminer la plateforme
      let platform = null;
      if (typeof window.cordova !== "undefined") {
        platform = window.cordova.platformId === 'ios' ? "ios" : "android";
      } else {
        platform = "web";
      }

      // Options pour la requête de publicité
      const options = {
        zoneId: zoneId,
        interstitial: true,
        size: `${width}x${height}`,
        platform: platform
      };

      const newAd = await sdk.models.AdServer.selectDisplayAd(options);
      console.log("Interstitial ad loaded:", newAd);
      
      if (newAd) {
        setAd(newAd);
        // Informer le parent qu'une pub valide a été chargée
        if (onAdLoadResult && !adLoadChecked) {
          onAdLoadResult(true);
          setAdLoadChecked(true);
        }
      } else {
        // Enregistrer qu'aucune publicité n'a été trouvée
        sdk.models.AdsImpression.addNoAd({
          zoneId: zoneId,
          size: `${width}x${height}`,
        });
        
        // Informer le parent qu'aucune pub n'a été trouvée
        if (onAdLoadResult && !adLoadChecked) {
          onAdLoadResult(false);
          setAdLoadChecked(true);
        }
        
        // Fermer automatiquement si aucune publicité n'est disponible et autoCloseOnNoAd est activé
        if (autoCloseOnNoAd) {
          setTimeout(() => onClose(), 500);
        }
      }
    } catch (error) {
      console.error("Failed to fetch interstitial ad:", error);
      
      // Informer le parent de l'échec
      if (onAdLoadResult && !adLoadChecked) {
        onAdLoadResult(false);
        setAdLoadChecked(true);
      }
      
      if (autoCloseOnNoAd) {
        setTimeout(() => onClose(), 500);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Charger la publicité une fois que les dimensions sont prêtes
  useEffect(() => {
    if (!ad && dimensionsReady && !adLoadChecked) {
      fetchAd();
    }
  }, [dimensionsReady, adLoadChecked]);

  // Gestion du décompte (seulement si showTimer est activé)
  useEffect(() => {
    if (showTimer && timer > 0 && !isLoading) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer, isLoading, showTimer]);

  // Gestion de la fermeture
  const handleClose = () => {
    if (!showTimer || timer === 0) {
      onClose();
    }
  };

  // Empêcher le défilement du corps pendant l'affichage de l'interstitiel (seulement pour le mode fullscreen)
  useEffect(() => {
    if (className.includes('ad-interstitial-container')) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [className]);

  const containerClassName = className || 'ad-interstitial-container';
  const containerStyle = {
    width: dimensions.width,
    height: dimensions.height,
    ...style
  };

  return (
    <div 
      ref={containerRef} 
      className={containerClassName}
      style={containerStyle}
    >
      {isLoading ? (
        <div className="ad-interstitial-loading">
          Chargement de l'annonce...
        </div>
      ) : ad ? (
        <>
          {showTimer && (
            <div 
              className={`ad-interstitial-timer ${timer === 0 ? 'clickable pulse' : ''}`} 
              onClick={handleClose}
            >
              {timer > 0 ? timer : "✖"}
            </div>
          )}
          
          <div className="ad-interstitial-content" style={{ width: dimensions.width, height: dimensions.height }}>
            {ad.type === "rtb_ad" ? (
              <RtbAd ad={ad} zoneId={zoneId} />
            ) : (
              <AdDisplay showPage={false} ad={ad} zoneId={zoneId} />
            )}
          </div>
        </>
      ) : (
        <div className="ad-interstitial-loading">
          {autoCloseOnNoAd ? "Aucune annonce disponible" : "Pas de publicité"}
        </div>
      )}
    </div>
  );
};

export { AdInterstitial };